// frontend/src/components/ProjectList.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import projectService from '../services/project/projectService';
import EditableProjectTitle from './EditableProjectTitle';
import languageService from '../services/language/languageService';
import '../styles/global.css';

// Language map with human-readable names
const ProjectList = () => {
    const [projects, setProjects] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [newProject, setNewProject] = useState({ title: '', source_language: '', target_language: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
        
    // States for delete confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);

    // Fetch projects when the user is logged in
    useEffect(() => {
        if (user) {
            fetchProjects();
            fetchLanguages();
        }
    }, [user]);

    const fetchLanguages = async () => {
        try {
            const languagesData = await languageService.getLanguages();
            setLanguages(languagesData);
        } catch (err) {
            console.error("Error fetching languages:", err);
            // Set some default languages in case of error
            setLanguages([
                { code: "en", name: "English" },
                { code: "fr", name: "French" },
                { code: "es", name: "Spanish" },
                { code: "de", name: "German" }
            ]);
        }
    };

    const fetchProjects = async () => {
        setLoading(true);
        setError('');
        try {
            // Use project service to get all projects
            const projectsData = await projectService.getAllProjects();
            
            if (projectsData.error) {
                throw new Error(projectsData.message || "Failed to fetch projects");
            }
            
            setProjects(projectsData);
        } catch (err) {
            console.error("Error fetching projects:", err);
            setError('Failed to fetch projects. Please ensure the backend server is running.');
        } finally {
            setLoading(false);
        }
    };

    const createProject = async () => {
        if (!newProject.title || !newProject.source_language || !newProject.target_language) {
            setError('Please fill in all fields');
            return;
        }

        if (newProject.source_language === newProject.target_language) {
            setError('Source and target languages must be different');
            return;
        }

        setLoading(true);
        setError('');
        try {
            // Use project service to create a new project
            const projectData = await projectService.createProject(newProject);
            
            if (projectData.error) {
                throw new Error(projectData.message || "Failed to create project");
            }
            
            setProjects([...projects, projectData]);
            setSuccess(`Project "${newProject.title}" created successfully`);
            setNewProject({ title: '', source_language: '', target_language: '' });
            
            // Clear success message after a few seconds
            setTimeout(() => {
                setSuccess('');
            }, 3000);
        } catch (err) {
            console.error("Error creating project:", err);
            setError('Failed to create project. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle clicking the delete button
    const handleDeleteClick = (project, e) => {
        e.stopPropagation(); // Prevent navigation to project view
        setProjectToDelete(project);
        setShowDeleteConfirm(true);
    };

    // Handle confirming project deletion
    const confirmDelete = async () => {
        if (!projectToDelete) return;

        setLoading(true);
        setError('');

        try {
            // Use project service to delete the project
            const result = await projectService.deleteProject(projectToDelete.id);
            
            if (result.error) {
                throw new Error(result.message || "Failed to delete project");
            }
            
            // Remove the project from the state
            setProjects(projects.filter(p => p.id !== projectToDelete.id));
            setSuccess(`Project "${projectToDelete.title}" deleted successfully`);

            // Reset confirmation state
            setProjectToDelete(null);
            setShowDeleteConfirm(false);

            // Clear success message after a few seconds
            setTimeout(() => {
                setSuccess('');
            }, 3000);
        } catch (err) {
            console.error("Error deleting project:", err);
            setError(err.message || "Failed to delete project");
        } finally {
            setLoading(false);
        }
    };

    // Cancel delete operation
    const cancelDelete = () => {
        setProjectToDelete(null);
        setShowDeleteConfirm(false);
    };

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!user && !localStorage.getItem('token')) {
            navigate('/login');
        }
    }, [user, navigate]);

    return (
        <div className="container">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Translation Projects</h1>
            </div>

            {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
            {success && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{success}</div>}

            {user && (
                <>
                    <div className="card mb-6">
                        <h2 className="text-xl font-semibold mb-4">Create New Project</h2>
                        
                        <div className="flex flex-row gap-4 mb-4 equal-height-inputs">
                            <div className="form-control flex-1">
                                <label className="form-label">Project Title</label>
                                <input
                                    className="form-input"
                                    value={newProject.title}
                                    onChange={e => setNewProject({ ...newProject, title: e.target.value })}
                                    placeholder="Enter project title"
                                />
                            </div>
                            
                            <div className="form-control flex-1">
                                <label className="form-label">Source Language</label>
                                <select
                                    className="form-select"
                                    value={newProject.source_language}
                                    onChange={e => setNewProject({ ...newProject, source_language: e.target.value })}
                                >
                                    <option value="">Select source language</option>
                                    {languages.map(lang => (
                                        <option key={`source-${lang.code}`} value={lang.code}>
                                            {lang.name} ({lang.code})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="form-control flex-1">
                                <label className="form-label">Target Language</label>
                                <select
                                    className="form-select"
                                    value={newProject.target_language}
                                    onChange={e => setNewProject({ ...newProject, target_language: e.target.value })}
                                >
                                    <option value="">Select target language</option>
                                    {languages.map(lang => (
                                        <option key={`target-${lang.code}`} value={lang.code}>
                                            {lang.name} ({lang.code})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        <div className="flex justify-end">
                            <button 
                                onClick={createProject}
                                disabled={loading || !newProject.title || !newProject.source_language || !newProject.target_language}
                                className="btn btn-primary"
                            >
                                {loading ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Creating...
                                    </span>
                                ) : (
                                    'Create Project'
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="card">
                        <h2 className="text-xl font-semibold mb-4">Your Projects</h2>
                        {loading && projects.length === 0 ? (
                            <div className="text-center p-4">
                                <div className="loading">Loading projects...</div>
                            </div>
                        ) : projects.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {projects.map(project => (
                                    <div 
                                        key={project.id} 
                                        className="card hover:shadow-lg mb-2"
                                    >
                                        <div className="flex justify-between items-center mb-3">
                                            <EditableProjectTitle
                                                projectId={project.id}
                                                initialTitle={project.title}
                                                onUpdate={(updatedProject) => {
                                                    setProjects(
                                                        projects.map(p =>
                                                            p.id === updatedProject.id ? { ...p, ...updatedProject } : p
                                                        )
                                                    );
                                                }}
                                                fontSize="text-lg"
                                                className="flex-grow mr-2"
                                            />
                                            
                                            <button
                                                onClick={(e) => handleDeleteClick(project, e)}
                                                className="btn-icon danger flex-shrink-0"
                                                title="Delete project"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                        
                                        <div 
                                            className="badge badge-primary text-xs mb-3"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                        {languages.find(l => l.code === project.source_language)?.name || project.source_language} →{' '}
                                        {languages.find(l => l.code === project.target_language)?.name || project.target_language}
                                        </div>

                                        <div className="mt-2">
                                            <a 
                                              href="#"
                                              onClick={(e) => { 
                                                e.preventDefault(); 
                                                navigate(`/projects/${project.id}`);
                                              }}
                                              className="nav-link"
                                            >
                                              Work on the project
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center p-6 bg-gray-50 rounded">
                                <p className="text-gray-500">No projects yet. Create one to get started!</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
                        <p className="mb-6">
                            Are you sure you want to delete the project <span className="font-semibold">{projectToDelete?.title}</span>?
                            This will also delete all documents, translations, glossaries, and instructions associated with this project.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelDelete}
                                className="btn btn-outline"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="btn btn-danger"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectList;